import React from 'react';
import {
  fetchOperationsDashboard,
  fetchOperationsHourly,
  refreshAnalyticsBalances,
  searchAnalyticsUsers,
  setAnalyticsUserExcluded,
} from '../api/admin';
import { getAdminLocale, localizeApiError, t } from '../i18n';
import './OperationsDashboard.css';

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const kstToday = () => new Date(Date.now() + KST_OFFSET_MS).toISOString().slice(0, 10);

function money(value, maxDigits = 2) {
  if (value == null) return t('데이터 없음');
  const n = Number(value || 0);
  return `${n.toLocaleString(getAdminLocale(), { minimumFractionDigits: 2, maximumFractionDigits: maxDigits })} USDT`;
}

function points(value) {
  if (value == null) return t('데이터 없음');
  const n = Number(value || 0);
  return `${n.toLocaleString(getAdminLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} P`;
}

function count(value) {
  const formatted = Number(value || 0).toLocaleString(getAdminLocale());
  return t('{count}명', { count: formatted });
}

function duration(ms) {
  if (ms == null) return t('데이터 없음');
  const totalSeconds = Math.max(0, Math.round(Number(ms || 0) / 1000));
  if (!totalSeconds) return t('{seconds}초', { seconds: 0 });
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sec = totalSeconds % 60;
  if (h > 0) return t('{hours}시간 {minutes}분 {seconds}초', { hours: h, minutes: m, seconds: sec });
  if (m > 0) return t('{minutes}분 {seconds}초', { minutes: m, seconds: sec });
  return t('{seconds}초', { seconds: sec });
}

function dateLabel(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return '-';
  return d.toLocaleString(getAdminLocale(), { timeZone: 'Asia/Seoul' });
}

function shiftDate(date, days) {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const HOURLY_METRICS = [
  { key: 'game_fee_amount', label: '본인 획득 게임 수수료', unit: 'USDT', digits: 6 },
  { key: 'downline_game_fee_amount', label: '하부 조직 게임 수수료', unit: 'USDT', digits: 6 },
  { key: 'organization_game_fee_amount', label: '조직 합계 게임 수수료 (본인 포함)', unit: 'USDT', digits: 6 },
  { key: 'deposit_amount', label: '입금(충전) 금액', unit: 'USDT', digits: 2 },
  { key: 'withdraw_amount', label: '출금(환전) 금액', unit: 'USDT', digits: 2 },
  { key: 'lounge_fee_amount', label: '라운지 거래 수수료', unit: 'USDT', digits: 2 },
  { key: 'user_event_fee_reward_amount', label: '유저 이벤트 수수료 획득량', unit: 'USDT', digits: 6 },
  { key: 'daily_mission_reward_amount', label: '데일리 미션 획득량', unit: 'USDT', digits: 2 },
  { key: 'table_mission_reward_amount', label: '테이블 미션 획득량', unit: 'USDT', digits: 2 },
  { key: 'leveling_mission_reward_amount', label: '레벨링 미션 획득량', unit: 'USDT', digits: 2 },
  { key: 'lounge_point_earned_amount', label: '라운지 포인트 획득량', unit: 'P', digits: 2 },
  { key: 'new_user_count', label: '신규 유저 수', unit: '명', integer: true },
  { key: 'deposit_user_count', label: '입금 유저 수', unit: '명', integer: true },
  { key: 'first_deposit_user_count', label: '최초 입금 유저 수', unit: '명', integer: true },
  { key: 'connected_user_count', label: '접속자 수', unit: '명', integer: true },
  { key: 'lobby_session_total_ms', label: '로비 총 체류시간', unit: '분', fromMs: true },
  { key: 'game_session_total_ms', label: '총 플레이시간', unit: '분', fromMs: true },
];

function hourlyMetricValue(row, metric) {
  const raw = Number(row?.[metric.key] || 0);
  return metric.fromMs ? raw / 60000 : raw;
}

function graphNumber(value, metric, compact = false) {
  const n = Number(value || 0);
  if (metric.integer) return `${Math.round(n).toLocaleString(getAdminLocale())}${t(metric.unit)}`;
  if (compact && Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (compact && Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n.toLocaleString(getAdminLocale(), { maximumFractionDigits: metric.digits ?? (metric.fromMs ? 1 : 2) })}${metric.unit ? ` ${t(metric.unit)}` : ''}`;
}

function HourlyLineChart({ rows, metric, rangeMode, storedRowCount = 0 }) {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const [tooltipPosition, setTooltipPosition] = React.useState(null);
  const points = (Array.isArray(rows) ? rows : []).map((row) => ({ row, value: hourlyMetricValue(row, metric) }));
  const width = 1000;
  const height = 300;
  const left = 62;
  const right = 18;
  const top = 22;
  const bottom = 42;
  const chartW = width - left - right;
  const chartH = height - top - bottom;
  const maxValue = Math.max(1, ...points.map((p) => p.value));
  const xFor = (i) => left + (points.length <= 1 ? chartW / 2 : (i / (points.length - 1)) * chartW);
  const yFor = (v) => top + chartH - (Math.max(0, v) / maxValue) * chartH;
  const polyline = points.map((p, i) => `${xFor(i).toFixed(2)},${yFor(p.value).toFixed(2)}`).join(' ');
  const tickIndexes = [];
  if (points.length) {
    const targetTicks = Math.min(8, points.length);
    for (let i = 0; i < targetTicks; i += 1) tickIndexes.push(Math.round((i * (points.length - 1)) / Math.max(1, targetTicks - 1)));
  }
  const values = points.map((p) => p.value);
  const total = values.reduce((a, b) => a + b, 0);
  const average = values.length ? total / values.length : 0;
  const peak = values.length ? Math.max(...values) : 0;
  const peakIndex = values.indexOf(peak);
  const peakRow = peakIndex >= 0 ? points[peakIndex]?.row : null;
  const hasNonZero = values.some((value) => Math.abs(Number(value || 0)) > 0);
  const xLabel = (row) => {
    if (!row) return '-';
    const hour = String(Number(row.stat_hour || 0)).padStart(2, '0');
    return rangeMode ? t('{date} {hour}시', { date: String(row.stat_date || '').slice(5), hour }) : t('{hour}시', { hour });
  };
  const tooltipDateLabel = (row) => {
    if (!row) return '-';
    const hour = String(Number(row.stat_hour || 0)).padStart(2, '0');
    return `${String(row.stat_date || '')} ${hour}:00 ~ ${hour}:59`;
  };
  const hoveredPoint = hoveredIndex == null ? null : points[hoveredIndex] || null;

  const handleMouseMove = (event) => {
    if (!points.length) return;
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const svgX = ((event.clientX - rect.left) / rect.width) * width;
    const svgY = ((event.clientY - rect.top) / rect.height) * height;
    if (svgX < left || svgX > width - right || svgY < top || svgY > top + chartH) {
      setHoveredIndex(null);
      setTooltipPosition(null);
      return;
    }
    const ratio = Math.max(0, Math.min(1, (svgX - left) / chartW));
    const index = points.length <= 1 ? 0 : Math.round(ratio * (points.length - 1));
    const tooltipWidth = 260;
    const tooltipHeight = 78;
    const gap = 14;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const x = event.clientX + gap + tooltipWidth > viewportWidth
      ? Math.max(8, event.clientX - tooltipWidth - gap)
      : event.clientX + gap;
    const y = event.clientY + gap + tooltipHeight > viewportHeight
      ? Math.max(8, event.clientY - tooltipHeight - gap)
      : event.clientY + gap;
    setHoveredIndex(index);
    setTooltipPosition({ x, y });
  };

  const clearHover = () => {
    setHoveredIndex(null);
    setTooltipPosition(null);
  };

  return (
    <div className="ops-hourly-chart-wrap">
      <div className="ops-hourly-stats">
        <span><small>{t('합계')}</small><strong>{graphNumber(total, metric)}</strong></span>
        <span><small>{t('시간당 평균')}</small><strong>{graphNumber(average, metric)}</strong></span>
        <span><small>{t('최고')}</small><strong>{graphNumber(peak, metric)} <em>{xLabel(peakRow)}</em></strong></span>
      </div>
      <div className="ops-hourly-svg-shell">
        <svg
          className="ops-hourly-svg"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={t('{metric} 시간대 그래프', { metric: t(metric.label) })}
          onMouseMove={handleMouseMove}
          onMouseLeave={clearHover}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = top + chartH - ratio * chartH;
            return (
              <g key={ratio}>
                <line x1={left} y1={y} x2={width - right} y2={y} className="ops-chart-grid" />
                <text x={left - 8} y={y + 4} textAnchor="end" className="ops-chart-axis-label">{graphNumber(maxValue * ratio, metric, true)}</text>
              </g>
            );
          })}
          {polyline && <polyline points={polyline} className="ops-chart-line" fill="none" />}
          {!hasNonZero && (
            <text x={left + chartW / 2} y={top + chartH / 2} textAnchor="middle" className="ops-chart-empty-label">
              {Number(storedRowCount || 0) > 0 ? t('선택한 항목의 시간대 값이 아직 없습니다.') : t('시간대 저장 데이터를 생성 중입니다.')}
            </text>
          )}
          {points.length <= 48 && points.map((p, i) => (
            <circle key={`${p.row.stat_date}-${p.row.stat_hour}`} cx={xFor(i)} cy={yFor(p.value)} r="2.4" className="ops-chart-dot" />
          ))}
          {hoveredPoint && (
            <g className="ops-chart-hover-guide" pointerEvents="none">
              <line
                x1={xFor(hoveredIndex)}
                y1={top}
                x2={xFor(hoveredIndex)}
                y2={top + chartH}
                className="ops-chart-hover-line"
              />
              <circle cx={xFor(hoveredIndex)} cy={yFor(hoveredPoint.value)} r="4.2" className="ops-chart-hover-dot" />
            </g>
          )}
          {tickIndexes.map((i) => {
            const p = points[i];
            if (!p) return null;
            return <text key={`x-${i}`} x={xFor(i)} y={height - 14} textAnchor="middle" className="ops-chart-x-label">{xLabel(p.row)}</text>;
          })}
        </svg>
      </div>
      {hoveredPoint && tooltipPosition && (
        <div
          className="ops-chart-tooltip"
          style={{ left: tooltipPosition.x, top: tooltipPosition.y }}
          role="status"
        >
          <div className="ops-chart-tooltip-time">{tooltipDateLabel(hoveredPoint.row)}</div>
          <div className="ops-chart-tooltip-row">
            <span>{t(metric.label)}</span>
            <strong>{graphNumber(hoveredPoint.value, metric)}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

function ExcludedUsersModal({ onClose, onChanged }) {
  const [query, setQuery] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [savingCode, setSavingCode] = React.useState('');
  const [error, setError] = React.useState('');

  const load = React.useCallback(async (q = '') => {
    setLoading(true);
    setError('');
    try {
      const res = await searchAnalyticsUsers(q, 50);
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(localizeApiError(e, '유저 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => void load(query), query ? 250 : 0);
    return () => clearTimeout(timer);
  }, [query, load]);

  const toggle = async (row) => {
    const userCode = String(row.user_code || '');
    if (!userCode || savingCode) return;
    const next = !row.excluded;
    setSavingCode(userCode);
    setError('');
    setRows((prev) => prev.map((item) => String(item.user_code) === userCode ? { ...item, excluded: next } : item));
    try {
      await setAnalyticsUserExcluded(userCode, next);
      onChanged?.();
    } catch (e) {
      setRows((prev) => prev.map((item) => String(item.user_code) === userCode ? { ...item, excluded: !next } : item));
      setError(localizeApiError(e, '제외 설정을 저장하지 못했습니다.'));
    } finally {
      setSavingCode('');
    }
  };

  return (
    <div className="ops-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="ops-modal" role="dialog" aria-modal="true" aria-labelledby="ops-exclude-title">
        <header className="ops-modal-header">
          <div>
            <p>ASSET EXCLUSION</p>
            <h2 id="ops-exclude-title">{t('자산 집계 제외 유저 설정')}</h2>
          </div>
          <button type="button" className="ops-icon-button" onClick={onClose} aria-label={t('닫기')}>×</button>
        </header>

        <p className="ops-modal-help">{t('체크한 유저는 전체 자산 집계에서 제외되며, 재무 대시보드의 일반/지정 유저 미션 상금 분류에도 적용됩니다. 라운지 포인트 통계에는 영향을 주지 않습니다.')}</p>

        <div className="ops-user-search">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('유저코드 / 아이디 / 닉네임 검색')}
          />
        </div>

        {error && <div className="ops-error">{error}</div>}

        <div className="ops-user-table-wrap">
          <table className="ops-user-table">
            <thead>
              <tr>
                <th>{t('제외')}</th>
                <th>{t('유저코드')}</th>
                <th>{t('아이디')}</th>
                <th>{t('닉네임')}</th>
                <th>{t('지갑+레이크백')}</th>
                <th>{t('라운지 포인트')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan="6" className="ops-empty">{t('불러오는 중…')}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="6" className="ops-empty">{t('검색 결과가 없습니다.')}</td></tr>
              ) : rows.map((row) => {
                const code = String(row.user_code || '');
                return (
                  <tr key={code || row.id} className="ops-user-mobile-row">
                    <td data-label={t('제외')}>
                      <label className="ops-check">
                        <input type="checkbox" aria-label={`${code} ${t('제외')}`} checked={Boolean(row.excluded)} disabled={savingCode === code} onChange={() => void toggle(row)} />
                        <span />
                      </label>
                    </td>
                    <td data-label={t('유저코드')}>{code}</td>
                    <td data-label={t('아이디')}>{row.id || '-'}</td>
                    <td data-label={t('닉네임')}>{row.nickname || '-'}</td>
                    <td data-label={t('지갑+레이크백')}>{Number(row.total_wallet_and_rakeback || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td data-label={t('라운지 포인트')}>{Number(row.lounge_point_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <footer className="ops-modal-footer">
          <span>{t('변경 즉시 자산 집계 및 조회 기간의 일반/지정 유저 미션 분류가 다시 계산됩니다.')}</span>
          <button type="button" className="ops-primary-button" onClick={onClose}>{t('완료')}</button>
        </footer>
      </section>
    </div>
  );
}

export default function OperationsDashboard() {
  const today = kstToday();
  const [mode, setMode] = React.useState('day');
  const [date, setDate] = React.useState(today);
  const [rangeStart, setRangeStart] = React.useState(today);
  const [rangeEnd, setRangeEnd] = React.useState(today);
  const [appliedRange, setAppliedRange] = React.useState({ startDate: today, endDate: today });
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState('');
  const [showExcludedUsers, setShowExcludedUsers] = React.useState(false);
  const [hourlyData, setHourlyData] = React.useState(null);
  const [hourlyLoading, setHourlyLoading] = React.useState(true);
  const [hourlyError, setHourlyError] = React.useState('');
  const [hourlyMetricKey, setHourlyMetricKey] = React.useState('game_fee_amount');

  const selection = React.useMemo(() => (
    mode === 'range'
      ? { mode: 'range', startDate: appliedRange.startDate, endDate: appliedRange.endDate }
      : { mode: 'day', date }
  ), [mode, date, appliedRange]);

  const load = React.useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await fetchOperationsDashboard(selection);
      setData(res?.data || null);
    } catch (e) {
      setError(localizeApiError(e, '운영 통계를 불러오지 못했습니다.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selection]);

  React.useEffect(() => { void load(false); }, [load]);

  const loadHourly = React.useCallback(async (silent = false) => {
    if (!silent) setHourlyLoading(true);
    setHourlyError('');
    try {
      const res = await fetchOperationsHourly(selection);
      setHourlyData(res?.data || null);
    } catch (e) {
      const code = e?.response?.data?.code;
      if (!silent) setHourlyData(null);
      setHourlyError(code === 'HOURLY_RANGE_TOO_LARGE'
        ? t('시간대 그래프는 한 번에 최대 90일까지 조회할 수 있습니다.')
        : localizeApiError(e, '시간대 통계를 불러오지 못했습니다.'));
    } finally {
      if (!silent) setHourlyLoading(false);
    }
  }, [selection]);

  React.useEffect(() => {
    void loadHourly(false);
    const timer = setInterval(() => void loadHourly(true), 60 * 1000);
    return () => clearInterval(timer);
  }, [loadHourly]);

  const useDayMode = () => {
    setMode('day');
  };

  const useRangeMode = () => {
    const base = date || today;
    setRangeStart(base);
    setRangeEnd(base);
    setAppliedRange({ startDate: base, endDate: base });
    setMode('range');
  };

  const applyRange = () => {
    setError('');
    if (!rangeStart || !rangeEnd) {
      setError(t('시작 날짜와 끝 날짜를 모두 선택해 주세요.'));
      return;
    }
    if (rangeStart > rangeEnd) {
      setError(t('시작 날짜는 끝 날짜보다 늦을 수 없습니다.'));
      return;
    }
    if (rangeEnd > today) {
      setError(t('오늘 이후 날짜는 선택할 수 없습니다.'));
      return;
    }
    setAppliedRange({ startDate: rangeStart, endDate: rangeEnd });
  };

  const goToday = () => {
    setDate(today);
    setMode('day');
  };

  const activeEndDate = mode === 'range' ? appliedRange.endDate : date;
  const displayPeriod = mode === 'range'
    ? `${appliedRange.startDate} ~ ${appliedRange.endDate}`
    : date;

  const refreshCurrentBalances = async () => {
    setRefreshing(true);
    setError('');
    try {
      await refreshAnalyticsBalances();
      await load(true);
    } catch (e) {
      setError(localizeApiError(e, '자산 집계를 새로고침하지 못했습니다.'));
    } finally {
      setRefreshing(false);
    }
  };

  const isRange = mode === 'range';
  const rows = data ? [
    { label: t('본인 획득 게임 수수료'), note: t('선택 기간 로그인한 LV 계정 본인에게 실제 지급된 게임 레이크 수수료 · 현상금 수수료 제외'), value: money(data.game_fee_amount, 6) },
    { label: t('하부 조직 게임 수수료'), note: t('본인 제외 · 현재 하부 관리자에게 지급된 게임 레이크 수수료 합계'), value: money(data.downline_game_fee_amount, 6) },
    { label: t('조직 합계 게임 수수료'), note: t('본인과 하부 관리자 획득분의 합계 · 중복 원장 제외'), value: money(data.organization_game_fee_amount, 6) },
    { label: t('총 입금(충전) 금액'), note: isRange ? t('선택 기간 CASHIER_DEPOSIT 실제 자산 반영 완료 합계') : t('CASHIER_DEPOSIT 실제 자산 반영 완료 기준'), value: money(data.deposit_amount) },
    { label: t('총 출금(환전) 금액'), note: isRange ? t('선택 기간 성공 처리된 출금 합계') : t('성공 처리된 출금 기준'), value: money(data.finance?.withdraw_amount ?? data.withdraw_amount) },
    { label: t(data?.scope ? '내 조직 유저 자산 보유량' : '전체 유저 자산 보유량'), note: isRange ? t('{date} 종료일 기준 · 유저 지갑 + 레이크백 + 착석 자산 / 제외 유저 미포함', { date: appliedRange.endDate }) : t('유저 지갑 + 레이크백 잔액 + 현재 테이블 착석 자산 / 제외 유저 미포함'), value: money(data.total_user_asset), action: !data?.scope ? <button type="button" className="ops-inline-button" onClick={() => setShowExcludedUsers(true)}>{t('제외 유저 설정')}</button> : null },
    { label: t('총 라운지 거래 수수료'), note: isRange ? t('선택 기간 라운지 결제 paid 기록 합계') : t('라운지 결제 paid 기록 기준'), value: money(data.lounge_fee_amount) },
    { label: t('총 유저 이벤트 수수료 획득량'), note: isRange ? t('선택 기간 유저에게 실제 지급된 이벤트/레이크백 수수료 합계') : t('유저에게 실제 지급된 이벤트/레이크백 수수료'), value: money(data.user_event_fee_reward_amount, 6) },
    { label: t('데일리 미션 금액 획득량'), value: money(data.daily_mission_reward_amount) },
    { label: t('모든 테이블 테이블 미션 총금액 획득량'), value: money(data.table_mission_reward_amount) },
    { label: t('레벨링 미션 총 금액 획득량'), value: money(data.leveling_mission_reward_amount) },
    { label: t('총 유저 라운지 포인트 획득량'), value: points(data.lounge_point_earned_amount) },
    { label: t(data?.scope ? '내 조직 유저 라운지 포인트 보유량' : '모든 유저 라운지 포인트 보유량'), note: isRange ? t('{date} 종료일 최종 보유량', { date: appliedRange.endDate }) : null, value: points(data.total_lounge_point_balance) },
    { label: t('신규 유저 가입 수'), note: isRange ? t('선택 기간 신규 가입자 합계') : null, value: count(data.new_user_count) },
    { label: t('충전유저 수'), note: isRange ? t('선택 기간 일별 성공 입금 고유 유저 수의 합계') : t('해당 날짜 성공 입금 고유 유저'), value: count(data.deposit_user_count) },
    { label: t('최초 입금 유저 수'), note: isRange ? t('생애 최초 입금일이 선택 기간에 포함된 유저 합계') : t('생애 최초 입금이 해당 날짜인 유저'), value: count(data.first_deposit_user_count) },
    { label: t('접속자 수'), note: isRange ? t('선택 기간 일별 로비 접속 고유 유저 수의 합계') : t('해당 날짜 로비에 접속한 고유 유저'), value: count(data.connected_user_count) },
    ...(!data?.scope ? [{ label: t('평균 체류시간'), note: isRange ? t('선택 기간 전체 로비 접속 세션 가중평균') : t('로비 접속 세션 평균'), value: duration(data.average_lobby_stay_ms) },
    { label: t('평균 플레이 시간'), note: isRange ? t('선택 기간 전체 게임 테이블 착석 세션 가중평균') : t('게임 테이블 착석 세션 평균'), value: duration(data.average_play_time_ms) }] : []),
  ] : [];

  const isOrganization = data?.scope?.type === 'organization';
  const finance = !isOrganization ? data?.finance || null : null;
  const positiveFinanceRows = finance ? [
    { label: t('슈퍼어드민 순수 게임 수수료'), note: t('LV1 최상위 계정 실제 레이크백 지급분 · 현상금 수수료 제외'), value: finance.root_game_rakeback_amount, precision: 6 },
    { label: t('잉여자금 · 매판 이벤트 수수료'), note: t('게임 정산 시 실제 차감된 이벤트 수수료 · USDT'), value: finance.per_hand_event_fee_amount },
  ] : [];
  const negativeFinanceRows = finance ? [
    { label: t('유저 환전(출금) 금액'), note: t('성공 처리된 실제 환전 총액'), value: finance.withdraw_amount },
    { label: t('일반 유저 미션 상금'), note: t('지정 유저 제외 · 데일리/테이블/레벨링/그룹 미션'), value: finance.regular_user_mission_reward_amount },
    { label: t('지정 유저 미션 상금'), note: t('제외 유저 설정에서 지정한 유저에게 지급된 미션 상금'), value: finance.designated_user_mission_reward_amount },
    { label: t('지정 유저 현상금 획득'), note: t('지정 유저의 현상금 실제 수령액 · 환불 제외'), value: finance.designated_user_bounty_reward_amount },
    { label: t('에이전트 테이블 미션 상금'), note: t('테이블 미션 플레이어 상금과 별도로 에이전트에게 실제 지급한 상금'), value: finance.agent_table_mission_reward_amount },
    ...(finance.unclassified_legacy_table_mission_amount > 0 ? [{
      label: t('수령자 미분류 과거 테이블 미션'),
      note: t('과거 기록에 수상자 정보가 없어 일반/지정 유저로 임의 분배하지 않은 금액'),
      value: finance.unclassified_legacy_table_mission_amount,
    }] : []),
  ] : [];

  const hourlyMetrics = data?.scope ? HOURLY_METRICS.filter((item) => !['lobby_session_total_ms', 'game_session_total_ms'].includes(item.key)) : HOURLY_METRICS;
  const hourlyMetric = hourlyMetrics.find((item) => item.key === hourlyMetricKey) || hourlyMetrics[0];
  const hourlyRows = Array.isArray(hourlyData?.rows) ? hourlyData.rows : [];

  return (
    <div className="ops-dashboard-page">
      <section className="ops-dashboard-card">
        <header className="ops-dashboard-header">
          <div>
            <p className="ops-eyebrow">OPERATIONS ANALYTICS</p>
            <h1>{t('운영 대시보드')}</h1>
            <span>{t('기본은 오늘 하루이며, 필요하면 시작일~종료일 기간 통계를 조회할 수 있습니다.')}</span>
          </div>

          <div className="ops-date-panel">
            <div className="ops-period-mode" role="group" aria-label={t('통계 기간 선택 방식')}>
              <button type="button" className={mode === 'day' ? 'active' : ''} onClick={useDayMode}>{t('하루')}</button>
              <button type="button" className={mode === 'range' ? 'active' : ''} onClick={useRangeMode}>{t('기간')}</button>
            </div>

            {mode === 'day' ? (
              <div className="ops-date-tools">
                <button type="button" className="ops-date-arrow" onClick={() => setDate((v) => shiftDate(v, -1))} aria-label={t('이전 날짜')}>‹</button>
                <input type="date" value={date} max={today} onChange={(e) => e.target.value && setDate(e.target.value)} />
                <button type="button" className="ops-date-arrow" onClick={() => setDate((v) => v < today ? shiftDate(v, 1) : v)} aria-label={t('다음 날짜')}>›</button>
                <button type="button" className="ops-secondary-button" onClick={goToday}>{t('오늘')}</button>
              </div>
            ) : (
              <div className="ops-range-tools">
                <label>
                  <span>{t('시작')}</span>
                  <input type="date" value={rangeStart} max={rangeEnd || today} onChange={(e) => e.target.value && setRangeStart(e.target.value)} />
                </label>
                <span className="ops-range-separator">~</span>
                <label>
                  <span>{t('끝')}</span>
                  <input type="date" value={rangeEnd} min={rangeStart || undefined} max={today} onChange={(e) => e.target.value && setRangeEnd(e.target.value)} />
                </label>
                <button type="button" className="ops-primary-button ops-range-apply" onClick={applyRange}>{t('조회')}</button>
                <button type="button" className="ops-secondary-button" onClick={goToday}>{t('오늘')}</button>
              </div>
            )}
          </div>
        </header>

        <div className="ops-status-row">
          <div>
            <strong>{displayPeriod}</strong>
            <span>{isRange ? t('선택 기간 · KST 기준') : t('KST 기준')}</span>
          </div>
          <div className="ops-status-actions">
            <span>{isRange ? t('종료일 자산 최종 집계') : t('자산 최종 집계')}: {data?.balance_data_available === false ? t('데이터 없음') : dateLabel(data?.balance_sampled_at)}</span>
            {activeEndDate === today && !isOrganization && (
              <button type="button" className="ops-secondary-button" disabled={refreshing} onClick={() => void refreshCurrentBalances()}>{refreshing ? t('갱신 중…') : t('자산 즉시 갱신')}</button>
            )}
          </div>
        </div>

        {isOrganization && (
          <div className="ops-status-row">
            <div><strong>{t('LV{level} · 내 조직 통계', { level: data.scope.level })}</strong>
              <span>{t('본인 포함 하위 관리자 {agents}명 / 소속 유저 {users}명 기준', { agents: data.scope.agent_count ?? 0, users: data.scope.user_count ?? 0 })}</span>
            </div>
            <span>{t('과거 시점의 조직별 보유 자산과 세션 평균은 원본 저장 방식상 제공되지 않습니다.')}</span>
          </div>
        )}
        {error && <div className="ops-error">{error}</div>}
        {data?.analytics_writer_health && data.analytics_writer_health.ok === false && (
          <div className="ops-error">{t('통계 기록 서버 연결 상태를 확인해 주세요. 일부 writer heartbeat가 감지되지 않았습니다.')}</div>
        )}

        {isOrganization && (
          <section className="ops-finance-section" aria-label="내 조직 운영 통계 요약">
            <div className="ops-finance-heading">
              <div>
                <p className="ops-eyebrow">ORGANIZATION · OPERATIONS</p>
                <h2>LV{data.scope.level} · 내 조직 운영 통계</h2>
                <small>본인과 하위 관리자·소속 유저의 선택 기간 실적입니다. 다른 조직의 금액은 포함되지 않습니다.</small>
              </div>
            </div>
            <div className="ops-finance-grids">
              <div className="ops-finance-group">
                <div className="ops-finance-group-title"><strong>거래 및 수수료</strong><span>+</span></div>
                {[
                  { label: '본인 획득 게임 수수료', note: '해당 관리자 본인에게 지급된 게임 레이크 수수료', value: data.game_fee_amount, precision: 6 },
                  { label: '하부 관리자 게임 수수료', note: '본인을 제외한 하부 관리자에게 지급된 금액', value: data.downline_game_fee_amount, precision: 6 },
                  { label: '조직 게임 수수료 합계', note: '본인과 하부 관리자 수수료의 합계', value: data.organization_game_fee_amount, precision: 6 },
                  { label: '소속 유저 충전 금액', note: '소속 유저 충전이 실제 자산에 반영된 금액', value: data.deposit_amount },
                  { label: '조직 라운지 거래 수수료', note: '조직 관리자에게 귀속된 라운지 수수료', value: data.lounge_fee_amount },
                ].map((item) => (
                  <div className="ops-finance-row" key={item.label}>
                    <div><strong>{item.label}</strong><small>{item.note}</small></div>
                    <span>{money(item.value, item.precision || 2)}</span>
                  </div>
                ))}
              </div>
              <div className="ops-finance-group">
                <div className="ops-finance-group-title"><strong>소속 유저 지급 및 환전</strong><span>−</span></div>
                {[
                  { label: '소속 유저 환전', note: '소속 유저가 실제 환전 완료한 금액', value: data.withdraw_amount },
                  { label: '데일리 미션 상금', note: '소속 유저 지급 내역', value: data.daily_mission_reward_amount },
                  { label: '테이블 미션 상금', note: '수령자가 기록된 소속 유저 지급 내역', value: data.table_mission_reward_amount },
                  { label: '레벨링 미션 상금', note: '소속 유저 지급 내역', value: data.leveling_mission_reward_amount },
                  { label: '이벤트 수수료 유저 지급', note: '소속 유저에게 지급된 이벤트·레이크백', value: data.user_event_fee_reward_amount, precision: 6 },
                ].map((item) => (
                  <div className="ops-finance-row" key={item.label}>
                    <div><strong>{item.label}</strong><small>{item.note}</small></div>
                    <span>{money(item.value, item.precision || 2)}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="ops-finance-footnote">
              충전액·수수료·환전액은 성격이 서로 달라 합산하여 순이익으로 표시하지 않습니다.
              LV1 전용 전체 사업 정산 및 지정 유저 현상금 수치는 하위 관리자에게 노출하지 않습니다.
              과거 테이블 미션 중 수령자 기록이 없는 금액은 조직별로 귀속할 수 없습니다.
            </p>
          </section>
        )}

        {!isOrganization && <section className="ops-finance-section" aria-label={t('슈퍼어드민 수지 현황')}>
          <div className="ops-finance-heading">
            <div>
              <p className="ops-eyebrow">SUPER ADMIN · FINANCE</p>
              <h2>{t('슈퍼어드민 수지 현황')}</h2>
              <small>{t('선택 기간 실제 원장 기준 · 지정 유저는 위 제외 유저 설정과 연동됩니다.')}</small>
            </div>
            <button type="button" className="ops-secondary-button" onClick={() => setShowExcludedUsers(true)}>{t('지정 유저 관리')}</button>
          </div>
          {loading ? (
            <div className="ops-finance-loading">{t('정산 데이터를 불러오는 중…')}</div>
          ) : finance ? (
            <>
              <div className="ops-finance-grids">
                <div className="ops-finance-group">
                  <div className="ops-finance-group-title"><strong>{t('플러스 · 수수료 / 잉여자금')}</strong><span>+</span></div>
                  {positiveFinanceRows.map((item) => (
                    <div className="ops-finance-row" key={item.label}>
                      <div><strong>{item.label}</strong><small>{item.note}</small></div>
                      <span className="ops-finance-positive">+{money(item.value, item.precision || 2)}</span>
                    </div>
                  ))}
                  <div className="ops-finance-subtotal"><strong>{t('플러스 합계')}</strong><span>{money(finance.positive_total, 6)}</span></div>
                </div>
                <div className="ops-finance-group">
                  <div className="ops-finance-group-title"><strong>{t('마이너스 · 실제 지급 / 환전')}</strong><span>−</span></div>
                  {negativeFinanceRows.map((item) => (
                    <div className="ops-finance-row" key={item.label}>
                      <div><strong>{item.label}</strong><small>{item.note}</small></div>
                      <span className="ops-finance-negative">−{money(item.value)}</span>
                    </div>
                  ))}
                  <div className="ops-finance-subtotal"><strong>{t('마이너스 합계')}</strong><span>{money(finance.negative_total)}</span></div>
                </div>
              </div>
              <div className="ops-finance-net">
                <div>
                  <small>{t('단순 수지 차액 · 회계상 순이익 아님')}</small>
                  <strong>{t('플러스 − 마이너스')}</strong>
                </div>
                <strong className={Number(finance.net_amount) < 0 ? 'ops-finance-negative' : 'ops-finance-positive'}>{money(finance.net_amount, 6)}</strong>
              </div>
              <div className="ops-finance-final" aria-label={t('최종 값')}>
                <div className="ops-finance-final-detail">
                  <small>{t('별도 계산식 · 선택 기간 기준')}</small>
                  <strong>{t('최종 값')}</strong>
                  <p>{t('(지정 유저 현상금 + LV1 게임 레이크백) − (지정 유저 제외 미션상금 + 유저 환전액)')}</p>
                </div>
                <strong className={Number(finance.final_value_amount) < 0 ? 'ops-finance-negative' : 'ops-finance-positive'}>{money(finance.final_value_amount, 6)}</strong>
              </div>
              <p className="ops-finance-footnote">
                {t('지정 유저 수')}: {Number(finance.designated_user_count || 0).toLocaleString(getAdminLocale())} · {t('지정 유저 미션은 일반 유저 미션에서 중복 차감하지 않습니다. 환전액은 미션 등에서 발생한 금액이 다시 출금된 경우도 포함되므로 이 차액은 회계 손익이 아닙니다.')}
                {!finance.payout_breakdown_complete && <strong>{' '}{t('과거 테이블 미션은 수령자별 내역이 없어 미분류로 표시됩니다.')}</strong>}
                {finance.unmatched_withdrawal_count > 0 && <strong>{' '}{t('환전 확정 원장에서 유저를 확인할 수 없는 내역이 있습니다. 최종 값이 실제와 다를 수 있으므로 확인이 필요합니다.')} ({finance.unmatched_withdrawal_count}{t('건')})</strong>}
                {finance.duplicate_game_fee_ledger_count > 0 && <strong>{' '}{t('동일 핸드 수수료 원장 중복이 확인되었습니다. 표시 합계는 최초 기록만 반영하며, 기존 중복 원장의 송금 상태는 별도 수동 확인이 필요합니다.')} ({finance.duplicate_game_fee_ledger_count}{t('건')})</strong>}
                {finance.event_fee_ledger_reconciliation_needed && <strong>{' '}{t('이벤트 수수료 원장 재기록 대기 또는 확인 불가: 표시 금액은 확정 원장 기준이며 실제 차감액보다 작을 수 있습니다.')} {finance.pending_game_fee_count != null && `(${finance.pending_game_fee_count}${t('건')} / ${money(finance.pending_game_event_fee_amount)} USDT)`}</strong>}
                {finance.local_pending_game_fee_count > 0 && <strong>{' '}{t('게임서버 로컬 복구 파일 대기: VPS3 미기록 수수료가 있습니다.')} ({finance.local_pending_game_fee_count}{t('건')} / {money(finance.local_pending_game_event_fee_amount)} USDT)</strong>}
                {finance.local_game_fee_status_unknown && <strong>{' '}{t('게임서버 로컬 복구 상태가 없거나 오래되었습니다. VPS3 연결·복구 상태를 확인하세요.')}{finance.local_game_fee_stale_node_count > 0 && ` (${finance.local_game_fee_stale_node_count}${t('건')})`}</strong>}
              </p>
            </>
          ) : <div className="ops-finance-loading">{t('정산 데이터를 표시할 수 없습니다.')}</div>}
        </section>}

        <div className="ops-summary-table-wrap">
          <table className="ops-summary-table">
            <thead><tr><th>{t('항목')}</th><th>{t('값')}</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="2" className="ops-empty">{t('통계를 불러오는 중…')}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="2" className="ops-empty">{t('표시할 통계가 없습니다.')}</td></tr>
              ) : rows.map((row, index) => (
                <tr key={row.label}>
                  <td>
                    <div className="ops-metric-name"><span className="ops-index">{String(index + 1).padStart(2, '0')}</span><div><strong>{row.label}</strong>{row.note && <small>{row.note}</small>}</div></div>
                  </td>
                  <td><div className="ops-value-cell"><strong>{row.value}</strong>{row.action}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <section className="ops-hourly-section">
          <div className="ops-hourly-header">
            <div>
              <p className="ops-eyebrow">HOURLY ANALYTICS</p>
              <h2>{t('시간대 분석')}</h2>
              <span>{t(data?.scope ? '내 조직 유저별 원장 및 접속 기록 기준으로 집계합니다.' : '일별 최종값 구조는 그대로 유지하고, 같은 cursor batch에서 KST 시간별 집계값을 함께 저장합니다.')}</span>
            </div>
            <label className="ops-hourly-metric-select">
              <span>{t('그래프 항목')}</span>
              <select value={hourlyMetricKey} onChange={(e) => setHourlyMetricKey(e.target.value)}>
                {hourlyMetrics.map((metric) => <option key={metric.key} value={metric.key}>{t(metric.label)}</option>)}
              </select>
            </label>
          </div>
          <div className="ops-hourly-period-note">
            <strong>{displayPeriod}</strong>
            <span> · {t('KST 1시간 단위')}</span>
            {hourlyData?.first_stored_hour && <span> · {t('저장 데이터 시작')}: {hourlyData.first_stored_hour}</span>}
          </div>
          {hourlyError ? (
            <div className="ops-error ops-hourly-error">{hourlyError}</div>
          ) : hourlyLoading ? (
            <div className="ops-hourly-loading">{t('시간대 통계를 불러오는 중…')}</div>
          ) : (
            <HourlyLineChart
              rows={hourlyRows}
              metric={hourlyMetric}
              rangeMode={isRange}
              storedRowCount={hourlyData?.stored_row_count || 0}
            />
          )}
          <p className="ops-hourly-footnote">{t('기존 일별/기간 통계 구조는 그대로 유지합니다. 과거 영구 원본이 남아 있는 금액·가입·최초입금 지표는 최근 90일까지 hourly 전용 cursor로 1회 백필하며, 접속/체류/플레이 시간은 시간대 기능 적용 이후 데이터부터 집계됩니다.')}</p>
        </section>

        <footer className="ops-dashboard-footer">
          <span>{isRange ? t('기간 통계는 날짜별 최종 집계값만 합산하여 조회합니다.') : t('통계는 날짜별 최종 집계 저장소에서 조회합니다.')}</span>
          <span>{t('마지막 통계 갱신')}: {dateLabel(data?.updated_at)}</span>
        </footer>
      </section>

      {showExcludedUsers && !isOrganization && (
        <ExcludedUsersModal
          onClose={() => setShowExcludedUsers(false)}
          onChanged={() => void load(true)}
        />
      )}
    </div>
  );
}
